import type { ReactNode } from 'react';
import { isAxiosError } from 'axios';
import { SurfaceState, type SurfaceStateProps } from './SurfaceState';
import { surfaceQueryState } from './surfaceQueryState';
import type { TruthfulStateKind } from './SurfaceState.types';

/**
 * The subset of a TanStack `useQuery` result this boundary reads. Typed
 * structurally so callers pass the query object directly.
 */
export interface QueryObservation<T> {
  data: T | undefined;
  status: 'pending' | 'error' | 'success';
  fetchStatus: 'fetching' | 'paused' | 'idle';
  error: unknown;
  refetch: () => Promise<unknown>;
}

/** HTTP status of a failed request, when the failure came from the server. */
export function queryErrorStatus(error: unknown): number | undefined {
  return isAxiosError(error) ? error.response?.status : undefined;
}

/** 401/403: the caller may not read this source. Never presented as empty. */
export function isPermissionError(error: unknown): boolean {
  const status = queryErrorStatus(error);
  return status === 401 || status === 403;
}

function defaultIsEmpty(data: unknown): boolean {
  if (Array.isArray(data)) return data.length === 0;
  return false;
}

/** Map a query to a truthful state kind ('ready' = render the data). */
export function querySurfaceKind<T>(
  query: QueryObservation<T>,
  isEmpty: (data: T) => boolean = defaultIsEmpty
): TruthfulStateKind | 'ready' {
  return surfaceQueryState({
    status: query.status,
    fetchStatus: query.fetchStatus,
    result: query.data === undefined ? 'missing' : isEmpty(query.data) ? 'empty' : 'present',
    permissionDenied: query.status === 'error' && isPermissionError(query.error),
  });
}

export interface QuerySurfaceStateProps<T> {
  query: QueryObservation<T>;
  /** Plural noun for copy, e.g. "unassigned tickets". */
  label: string;
  /** Rendered only when the source answered with non-empty data. */
  children: (data: T) => ReactNode;
  /** Overrides the default "array has no rows" test. */
  isEmpty?: (data: T) => boolean;
  emptyTitle?: string;
  emptyDescription?: string;
  /** Optional create/configure action on a verified-empty result. */
  emptyAction?: SurfaceStateProps['action'];
  /** Copy for a disabled query that is waiting for input (e.g. no customer chosen). */
  idleTitle?: string;
  idleDescription?: string;
  loadingVariant?: SurfaceStateProps['loadingVariant'];
  className?: string;
}

/**
 * The one boundary between a query and its list/table/card body. It renders
 * loading, permission, unavailable (with retry), and verified-empty states via
 * {@link SurfaceState}, so a failed, unauthorized, missing-route or malformed
 * response can never be shown as "No items". See docs/uix/TRUTHFUL_STATES.md.
 */
export function QuerySurfaceState<T>({
  query,
  label,
  children,
  isEmpty,
  emptyTitle,
  emptyDescription,
  emptyAction,
  idleTitle,
  idleDescription,
  loadingVariant,
  className,
}: QuerySurfaceStateProps<T>) {
  const kind = querySurfaceKind(query, isEmpty);
  if (kind === 'ready') return <>{children(query.data as T)}</>;

  const retry = { label: `Retry ${label}`, onClick: query.refetch };
  const status = queryErrorStatus(query.error);

  if (kind === 'stale' || kind === 'retrying') {
    // Cached rows exist but the latest check failed: show them, labelled stale.
    return (
      <>
        <SurfaceState
          kind={kind}
          title={`Showing previously loaded ${label}`}
          description="The latest refresh failed, so these rows may be out of date."
          action={kind === 'stale' ? retry : undefined}
          className={className}
        />
        {query.data !== undefined ? children(query.data) : null}
      </>
    );
  }

  const copy: Record<string, { title: string; description: string }> = {
    loading: { title: `Loading ${label}`, description: 'Checking the source.' },
    idle: {
      title: idleTitle ?? `Choose what to show to load ${label}`,
      description:
        idleDescription ?? `Nothing has been requested yet, so no ${label} are shown.`,
    },
    permission: {
      title: `You do not have access to ${label}`,
      description: 'Your role cannot read this source, so nothing is shown. No empty result was assumed.',
    },
    'verified-empty': {
      title: emptyTitle ?? `No ${label}`,
      description: emptyDescription ?? `The source was checked and returned no ${label}.`,
    },
    unavailable: {
      title: `${label.charAt(0).toUpperCase()}${label.slice(1)} could not be loaded`,
      description:
        status === 404
          ? 'This server does not provide this source (404). No empty result was assumed.'
          : 'The source could not be verified, so nothing is shown. No empty result was assumed.',
    },
  };
  const { title, description } = copy[kind] ?? copy.unavailable;

  return (
    <SurfaceState
      kind={kind}
      title={title}
      description={description}
      action={
        kind === 'verified-empty'
          ? emptyAction
          : kind === 'loading' || kind === 'permission' || kind === 'idle'
            ? undefined
            : retry
      }
      loadingVariant={loadingVariant}
      className={className}
    />
  );
}
